import type { MarketIdentifiers } from '$lib/types/market';
import BigNumber from 'bignumber.js';
import type { UserMargin } from '$lib/types/user';
import { ethers } from 'ethers';
import { addMargin, removeMargin } from '../connectors/margin';
import type { IBuilderReturnData } from '$lib/web3/types';

export const buildAddMarginCallData = (
	marginDelta: string,
	marketIdentifier: MarketIdentifiers
): IBuilderReturnData => {
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	const addMarginCallData = addMargin(marketIdentifier, marginDelta);

	targetArray.push(addMarginCallData._target);
	callDataArray.push(addMarginCallData._callData);

	return {
		_targetArray: targetArray,
		_callDataArray: callDataArray
	};
};

export const buildRemoveMarginCallData = (
	marginDelta: string,
	marketIdentifier: MarketIdentifiers
): IBuilderReturnData => {
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	const removeMarginCallData = removeMargin(marketIdentifier, marginDelta);
	targetArray.push(removeMarginCallData._target);
	callDataArray.push(removeMarginCallData._callData);
	return {
		_targetArray: targetArray,
		_callDataArray: callDataArray
	};
};

export function buildMarginCallData(
	marginsWithoutPositions: Array<UserMargin>,
	marketIdentifier: MarketIdentifiers,
	marginDelta: string,
	AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION: boolean
) {
	const targetArray: Array<string> = [];
	const callData: Array<string> = [];

	if (marginsWithoutPositions.length > 0 && AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION) {
		const { _targets, _callDatas } = removeMarginFromOtherMarkets(
			marginsWithoutPositions,
			marketIdentifier
		);
		targetArray.push(..._targets);
		callData.push(..._callDatas);
	}

	if (!new BigNumber(marginDelta).isZero()) {
		const { _target, _callData } = addMargin(marketIdentifier, marginDelta);
		targetArray.push(_target);
		callData.push(_callData);
	}
	return { _targetArray: targetArray, _callData: callData };
}

export function removeMarginFromOtherMarkets(
	marginsWithoutPositions: Array<UserMargin>,
	tradingMarketIdentifier: MarketIdentifiers | '' // empty when no market is selected
) {
	let targetArray: Array<string> = [];
	let callData: Array<string> = [];
	for (let i = 0; i < marginsWithoutPositions.length; i++) {
		const { marketIdentifier, accessibleMargin, depositedMargin } = marginsWithoutPositions[i];

		if (
			new BigNumber(accessibleMargin).isEqualTo(depositedMargin) &&
			marketIdentifier !== tradingMarketIdentifier &&
			!new BigNumber(accessibleMargin).isZero()
		) {
			let marginToRemove = ethers.constants.MaxUint256.toString();

			const { _target, _callData } = removeMargin(
				marketIdentifier as MarketIdentifiers,
				marginToRemove
			);

			targetArray = targetArray.concat(_target);
			callData = callData.concat(_callData);
		}
	}
	return { _targets: targetArray, _callDatas: callData };
}
